// src/components/BillingForm.js
import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  serverTimestamp,
  orderBy,
  onSnapshot,
  writeBatch
} from 'firebase/firestore';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Import MUI
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  TextField,
  Autocomplete,
  Grid,
  Divider,
  IconButton,
  Alert
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';

// Funzione helper per formattare la valuta (invariata)
const formatCurrency = (value, lang) => {
  const locale = lang === 'it' ? 'it-IT' : 'en-US';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
  }).format(value || 0);
};

// Hook custom per fetchare collezioni (invariato)
const useFirestoreCollection = (collectionName, teamId) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!teamId) {
      setLoading(false);
      return;
    }
    let q;
    if (collectionName === 'drivers') {
      q = query(collection(db, "drivers"), where("teamId", "==", teamId), orderBy("nomeCompleto", "asc"));
    } else if (collectionName === 'events') {
      // CORREZIONE: Puntava a "eventsCalendar" e ordinava per "start"
      q = query(collection(db, "events"), where("teamId", "==", teamId), orderBy("startDate", "desc"));
    } else {
      setLoading(false);
      return;
    }
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setData(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    }, () => setLoading(false));
    return () => unsubscribe();
  }, [collectionName, teamId]);
  return { data, loading };
};


function BillingForm({ teamId, userRole, userId }) {
  const navigate = useNavigate();
  const { summaryId } = useParams();
  const isEditMode = Boolean(summaryId);
  
  const { t, i18n } = useTranslation('billing');
  const currentLang = i18n.language;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null); // <-- 1. Aggiunto stato per avvisi
  const [isLoadingData, setIsLoadingData] = useState(false);

  const [titolo, setTitolo] = useState('');
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null); 
  const [manualItems, setManualItems] = useState([{ description: '', amount: 0 }]);
  const [importedData, setImportedData] = useState({
    laborItems: [],
    partsItems: [],
    totalLaborCost: 0,
    totalPartsCost: 0,
    sourceWoIds: [],
  });

  const { data: driversList, loading: isLoadingDrivers } = useFirestoreCollection('drivers', teamId);
  const { data: eventsList, loading: isLoadingEvents } = useFirestoreCollection('events', teamId);


  // useEffect per Edit Mode (invariato)
  useEffect(() => {
    if (isEditMode && driversList.length > 0 && eventsList.length > 0) {
      setIsLoadingData(true);
      const docRef = doc(db, "billingSummaries", summaryId);
      getDoc(docRef).then(docSnap => {
        if (docSnap.exists() && docSnap.data().teamId === teamId) {
          const data = docSnap.data();
          setTitolo(data.titolo);
          const driver = driversList.find(d => d.id === data.pilotaId) || null;
          setSelectedDriver(driver);
          if (data.eventoId) {
             const event = eventsList.find(e => e.id === data.eventoId) || null;
             setSelectedEvent(event);
          }
          setImportedData({
            laborItems: data.lineItems_labor || [],
            partsItems: data.lineItems_parts || [],
            totalLaborCost: data.totals?.labor || 0,
            totalPartsCost: data.totals?.parts || 0,
          });
          setManualItems(data.lineItems_manual && data.lineItems_manual.length > 0 ? data.lineItems_manual : [{ description: '', amount: 0 }]);
        } else {
          setError(t('detail.error_not_found'));
        }
        setIsLoadingData(false);
      }).catch(err => {
        console.error("Error loading summary for edit:", err);
        setError(t('detail.error_loading'));
        setIsLoadingData(false);
      });
    }
  }, [isEditMode, summaryId, teamId, driversList, eventsList, t]);


  // Funzioni righe manuali (invariate)
  const handleAddManualItem = () => {
    setManualItems([...manualItems, { description: '', amount: 0 }]);
  };
  const handleRemoveManualItem = (index) => {
    const newList = manualItems.filter((_, i) => i !== index);
    setManualItems(newList);
  };
  const handleManualItemChange = (index, field, value) => {
    const newList = [...manualItems];
    newList[index][field] = field === 'amount' ? Number(value) : value;
    setManualItems(newList);
  };

  // --- 2. Funzione di importazione AGGIORNATA ---
  const handleImportCosts = async () => {
    if (!selectedDriver) {
      setError(t('form.import_error_driver')); 
      return;
    }
    
    setIsImporting(true);
    setError(null);
    setWarning(null); // Resetta avvisi
    setImportedData({ laborItems: [], partsItems: [], totalLaborCost: 0, totalPartsCost: 0, sourceWoIds: [] });

    try {
      // 1. Ottieni costo orario (invariato)
      const teamDocRef = doc(db, "teams", teamId);
      const teamSnap = await getDoc(teamDocRef);
      const teamData = teamSnap.data();
      const costoOrario = teamData?.costoOrario || 0;
      if (costoOrario === 0) {
        console.warn("Team hourly rate (costoOrario) is not set. Labor cost will be 0.");
      }

      // 2. Trova TUTTI i veicoli del pilota (invariato)
      const vehiclesQuery = query(
        collection(db, "vehicles"),
        where("teamId", "==", teamId),
        where("mainDriverId", "==", selectedDriver.id)
      );
      const vehiclesSnap = await getDocs(vehiclesQuery);
      
      if (vehiclesSnap.empty) {
        throw new Error(t('form.import_error_vehicle', { driverName: selectedDriver.nomeCompleto })); 
      }
      
      const vehicleIds = vehiclesSnap.docs.map(doc => doc.id);
      
      // 3. Costruisci le query per i WorkOrders
      
      // Definiamo i filtri di base
      const baseConstraints = [
        where("teamId", "==", teamId),
        where("vehicleId", "in", vehicleIds), 
        where("status", "==", "Completed")
      ];
      
      // Aggiungi il filtro evento SOLO se è stato selezionato
      if (selectedEvent) {
        baseConstraints.push(where("eventId", "==", selectedEvent.id));
      }
      
      // Esegui la singola query di base per evitare problemi con indici compositi 
      // e campi 'null' (billingSummaryId potrebbe non esistere nei vecchi documenti)
      const baseQuery = query(collection(db, "workOrders"), ...baseConstraints);
      const allOrdersSnap = await getDocs(baseQuery);

      // 5. Analizza i risultati (filtra lato client)
      const availableOrdersDocs = [];
      let billedCount = 0;

      allOrdersSnap.forEach(docSnap => {
        const data = docSnap.data();
        if (data.billingSummaryId) {
          billedCount++; 
        } else {
          availableOrdersDocs.push(docSnap);
        }
      });
      
      // SCENARIO A: Ci sono ordini disponibili da importare
      if (availableOrdersDocs.length > 0) {
        
        // (Opzionale) Se ci sono anche ordini già fatturati, mostra un avviso
        if (billedCount > 0) {
          setWarning(t('form.import_warning_skipped', { count: billedCount }));
        }

        // Processa i dati
        let totalHours = 0;
        const allPartsItems = [];
        const allLaborItems = [];
        const allImportedWoIds = [];
        
        for (const woDoc of availableOrdersDocs) {
          allImportedWoIds.push(woDoc.id);
          const itemsQuery = query(collection(db, "workOrders", woDoc.id, "items"));
          const itemsSnap = await getDocs(itemsQuery);
          
          // eslint-disable-next-line no-loop-func
          itemsSnap.forEach(itemDoc => {
            const item = itemDoc.data();
            const hours = item.hours || 0;
            totalHours += hours;
            allLaborItems.push({
              sourceWorkOrderId: woDoc.id, 
              sourceWorkOrderItemId: itemDoc.id, 
              taskDescription: item.description,
              ore: hours,
              costoOrario: hours === 0 ? 0 : costoOrario,
              total: hours * costoOrario
            });

            const parts = item.parts || [];
            parts.forEach(part => {
              const partTotal = (part.quantity || 0) * (part.prezzoVendita || 0);
              allPartsItems.push({
                sourceWorkOrderId: woDoc.id, 
                sourceWorkOrderItemId: itemDoc.id, 
                partId: part.id,
                name: part.name,
                quantity: part.quantity,
                prezzoVenditaUnitario: part.prezzoVendita,
                total: partTotal
              });
            });
          });
        }
        
        const totalPartsCost = allPartsItems.reduce((sum, item) => sum + item.total, 0);
        const totalLaborCost = totalHours * costoOrario;
        setImportedData({
          laborItems: allLaborItems,
          partsItems: allPartsItems,
          totalLaborCost: totalLaborCost,
          totalPartsCost: totalPartsCost,
          sourceWoIds: allImportedWoIds,
        });

      } 
      // SCENARIO B: NON ci sono ordini disponibili
      else {
        
        // B1: Non ci sono disponibili, MA ce ne sono di già fatturati
        if (billedCount > 0) {
          // Questo è il tuo caso! Mostra un errore specifico.
          throw new Error(t('form.import_error_already_billed', { count: billedCount }));
        } 
        // B2: Non c'è nulla, né disponibile né fatturato
        else {
          // Errore originale: non c'è proprio nulla di completato
          const errorKey = selectedEvent ? 'form.import_error_no_orders_event' : 'form.import_error_no_orders';
          throw new Error(t(errorKey)); 
        }
      }

    } catch (err) {
      console.error("Error importing costs:", err);
      setError(err.message); 
    } finally {
      setIsImporting(false);
    }
  };
  // --- FINE MODIFICA ---

  // Funzione di Salvataggio (invariata)
  const handleSaveSummary = async (status = 'draft') => {
    if (!titolo || !selectedDriver) {
      setError(t('form.error_required')); 
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    setWarning(null); // Pulisce gli avvisi al salvataggio
    
    const totalManual = manualItems.reduce((sum, item) => sum + (item.amount || 0), 0);
    const grandTotal = importedData.totalLaborCost + importedData.totalPartsCost + totalManual;

    try {
      const summaryData = {
        teamId: teamId,
        pilotaId: selectedDriver.id,
        pilotaName: selectedDriver.nomeCompleto,
        eventoId: selectedEvent ? selectedEvent.id : null,
        eventName: selectedEvent ? selectedEvent.eventName : null, 
        titolo: titolo,
        status: status,
        createdBy: userId,
        lineItems_parts: importedData.partsItems,
        lineItems_labor: importedData.laborItems,
        lineItems_manual: manualItems.filter(item => item.description && item.amount > 0)
                                    .map(item => ({ description: item.description, amount: item.amount })),
        totals: {
          parts: importedData.totalPartsCost,
          labor: importedData.totalLaborCost,
          manual: totalManual,
          grandTotal: grandTotal
        }
      };

      if (isEditMode) {
        const docRef = doc(db, "billingSummaries", summaryId);
        await updateDoc(docRef, {
          ...summaryData,
          updatedAt: serverTimestamp()
        });
        navigate(`/billing/${summaryId}`);
      } else {
        const importedWorkOrderIds = importedData.sourceWoIds || [];
        summaryData.importedWorkOrderIds = [...new Set(importedWorkOrderIds)]; 
        
        const docRef = await addDoc(collection(db, "billingSummaries"), {
          ...summaryData,
          createdAt: serverTimestamp()
        });

        const batch = writeBatch(db);
        summaryData.importedWorkOrderIds.forEach(woId => {
          const woRef = doc(db, "workOrders", woId);
          batch.update(woRef, { billingSummaryId: docRef.id });
        });
        await batch.commit();
        
        navigate(`/billing/${docRef.id}`);
      }
      
    } catch (err) {
      console.error("Error saving summary:", err);
      setError(t('form.error_save')); 
      setIsSubmitting(false);
    }
  };
  
  const totalManual = manualItems.reduce((sum, item) => sum + (item.amount || 0), 0);
  const grandTotal = importedData.totalLaborCost + importedData.totalPartsCost + totalManual;

  if (isEditMode && isLoadingData) {
     return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>{t('form.loading')}</Typography>
      </Box>
    );
  }

  return (
    <>
      <Paper 
        elevation={0} 
        sx={{ 
          p: 3, 
          mb: 3, 
          backgroundColor: 'background.paper', 
          border: '1px solid', 
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate('/billing')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
            {isEditMode ? t('form.title_edit') : t('form.title_new')}
          </Typography>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Paper elevation={0} sx={paperStyle}>
            <Typography variant="h6" gutterBottom>{t('form.section1')}</Typography>
            <TextField
              label={t('form.title_label')}
              value={titolo}
              onChange={(e) => setTitolo(e.target.value)}
              fullWidth
              required
              sx={{ mb: 2.5 }}
            />
            <Autocomplete
              options={driversList}
              getOptionLabel={(option) => option.nomeCompleto || ''}
              value={selectedDriver}
              onChange={(event, newValue) => setSelectedDriver(newValue)}
              isOptionEqualToValue={(option, value) => (value ? option.id === value.id : false)}
              loading={isLoadingDrivers}
              renderInput={(params) => <TextField {...params} label={t('form.driver_label')} required />}
              sx={{ mb: 2.5 }}
              disabled={isImporting || isEditMode} 
            />
            <Autocomplete
              options={eventsList}
              getOptionLabel={(option) => option.eventName || t('form.event_unnamed')}
              value={selectedEvent}
              onChange={(event, newValue) => setSelectedEvent(newValue)}
              isOptionEqualToValue={(option, value) => (value ? option.id === value.id : false)}
              loading={isLoadingEvents}
              renderInput={(params) => <TextField {...params} label={t('form.event_label')} helperText={t('form.event_helper')} />}
              sx={{ mb: 2.5 }}
              disabled={isImporting || isEditMode} 
            />
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="h6" gutterBottom>{t('form.section2')}</Typography>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleImportCosts}
              disabled={isImporting || !selectedDriver || isEditMode}
              startIcon={isImporting ? <CircularProgress size={20} /> : <DownloadForOfflineIcon />}
              sx={{ mb: 2 }}
            >
              {t('form.import_button')}
            </Button>
            {isEditMode && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('form.import_disabled_edit')}
              </Alert>
            )}
            
            {/* 3. Mostra l'avviso o l'errore */}
            {warning && <Alert severity="warning" sx={{ mt: 2 }}>{warning}</Alert>}
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6" gutterBottom>{t('form.section3')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('form.manual_subtitle')}
            </Typography>
            
            {manualItems.map((item, index) => (
              <Box key={index} sx={{ display: 'flex', gap: 2, mb: 1.5, alignItems: 'center' }}>
                <TextField
                  label={t('form.manual_desc')}
                  value={item.description || ''}
                  onChange={(e) => handleManualItemChange(index, 'description', e.target.value)}
                  sx={{ flex: 3 }}
                />
                <TextField
                  label={t('form.manual_amount')}
                  type="number"
                  value={item.amount || 0}
                  onChange={(e) => handleManualItemChange(index, 'amount', e.target.value)}
                  inputProps={{ step: "0.01", min: "0" }}
                  sx={{ flex: 1 }}
                />
                <IconButton onClick={() => handleRemoveManualItem(index)} color="error">
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddManualItem}
              size="small"
            >
              {t('form.add_row')}
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper elevation={0} sx={{ ...paperStyle, position: 'sticky', top: 80 }}>
            <Typography variant="h5" gutterBottom>{t('form.summary_title')}</Typography>
            
            <Box sx={summaryRowStyle}>
              <Typography>{t('form.summary_labor')}</Typography>
              <Typography>{formatCurrency(importedData.totalLaborCost, currentLang)}</Typography>
            </Box>
            <Box sx={summaryRowStyle}>
              <Typography>{t('form.summary_parts')}</Typography>
              <Typography>{formatCurrency(importedData.totalPartsCost, currentLang)}</Typography>
            </Box>
            <Box sx={summaryRowStyle}>
              <Typography>{t('form.summary_manual')}</Typography>
              <Typography>{formatCurrency(totalManual, currentLang)}</Typography>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box sx={summaryRowStyle}>
              <Typography variant="h5" component="p" sx={{ fontWeight: 700 }}>{t('form.summary_total')}</Typography>
              <Typography variant="h5" component="p" sx={{ fontWeight: 700 }}>
                {formatCurrency(grandTotal, currentLang)}
              </Typography>
            </Box>
            
            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              {(!isEditMode || (isEditMode && manualItems.some(item => item.status !== 'finalized'))) && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => handleSaveSummary('draft')}
                  disabled={isSubmitting}
                  startIcon={<SaveIcon />}
                  sx={{ flex: 1 }}
                >
                  {isEditMode ? t('form.save_changes') : t('form.save_draft')}
                </Button>
              )}
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleSaveSummary('finalized')}
                disabled={isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={20} /> : <SaveIcon />}
                sx={{ flex: 1 }}
              >
                {isEditMode ? t('form.update_finalize') : t('form.finalize')}
              </Button>
            </Box>
          </Paper>
        </Grid>
        
      </Grid>
    </>
  );
}

const paperStyle = {
  p: { xs: 2, sm: 3 }, 
  border: '1px solid',
  borderColor: 'divider',
  backgroundColor: 'background.paper'
};
const summaryRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  mb: 1
};

export default BillingForm;